import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Tooltip,
  CircularProgress,
  Chip,
  LinearProgress,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import { attachmentsApi } from '../../api/endpoints/attachmentsApi';
import { TaskAttachment } from '../../api/types/AttachmentTypes';

interface TaskAttachmentsProps {
  taskId: string;
}

export const TaskAttachments: React.FC<TaskAttachmentsProps> = ({ taskId }) => {
  const [attachments, setAttachments] = useState<TaskAttachment[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [thumbnails, setThumbnails] = useState<Record<string, string>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadAttachments = useCallback(async () => {
    setLoading(true);
    try {
      const data = await attachmentsApi.getTaskAttachments(taskId);
      setAttachments(data);
    } catch (error) {
      console.error('Failed to load attachments:', error);
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  useEffect(() => {
    loadAttachments();
  }, [loadAttachments]);

  // Attachments are served behind an authenticated download endpoint (no
  // public file URL to drop into an <img src>), so image thumbnails have to
  // be fetched as blobs and turned into object URLs, one per image attachment.
  useEffect(() => {
    const imageAttachments = attachments.filter((a) => a.mimeType?.startsWith('image/'));
    let cancelled = false;

    imageAttachments.forEach((attachment) => {
      if (thumbnails[attachment.id]) return;
      attachmentsApi
        .downloadAttachment(taskId, attachment.id)
        .then((blob) => {
          if (cancelled) return;
          const url = URL.createObjectURL(blob);
          setThumbnails((prev) => ({ ...prev, [attachment.id]: url }));
        })
        .catch((error) => {
          console.error('Failed to load thumbnail:', error);
        });
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachments, taskId]);

  // Revoke every object URL on unmount to avoid leaking them.
  useEffect(() => {
    return () => {
      Object.values(thumbnails).forEach((url) => URL.revokeObjectURL(url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => Math.min(prev + 10, 90));
      }, 200);

      const attachment = await attachmentsApi.uploadAttachment(taskId, file);

      clearInterval(progressInterval);
      setUploadProgress(100);

      setAttachments([...attachments, attachment]);

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Failed to upload file. Please try again.');
    } finally {
      setTimeout(() => {
        setUploading(false);
        setUploadProgress(0);
      }, 500);
    }
  };

  const handleDownload = async (attachmentId: string, fileName: string) => {
    try {
      const blob = await attachmentsApi.downloadAttachment(taskId, attachmentId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Download failed:', error);
      alert('Failed to download file. Please try again.');
    }
  };

  const handleDelete = async (attachmentId: string) => {
    if (!window.confirm('Are you sure you want to delete this attachment?')) {
      return;
    }

    try {
      await attachmentsApi.deleteAttachment(taskId, attachmentId);
      setAttachments(attachments.filter((a) => a.id !== attachmentId));
      setThumbnails((prev) => {
        const { [attachmentId]: removedUrl, ...rest } = prev;
        if (removedUrl) URL.revokeObjectURL(removedUrl);
        return rest;
      });
    } catch (error) {
      console.error('Delete failed:', error);
      alert('Failed to delete attachment. Please try again.');
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileIcon = (mimeType: string | null) => {
    if (!mimeType) return '📎';
    if (mimeType.startsWith('image/')) return '🖼️';
    if (mimeType.startsWith('video/')) return '🎥';
    if (mimeType.startsWith('audio/')) return '🎵';
    if (mimeType.includes('pdf')) return '📄';
    if (mimeType.includes('word') || mimeType.includes('document')) return '📝';
    if (mimeType.includes('sheet') || mimeType.includes('excel')) return '📊';
    if (mimeType.includes('zip') || mimeType.includes('archive')) return '📦';
    return '📎';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper elevation={1} sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Attachments ({attachments.length})
        </Typography>
        <Button
          variant="contained"
          startIcon={<AttachFileIcon />}
          onClick={handleFileSelect}
          disabled={uploading}
        >
          Upload File
        </Button>
      </Box>

      <input
        ref={fileInputRef}
        type="file"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      {uploading && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress variant="determinate" value={uploadProgress} />
          <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
            Uploading... {uploadProgress}%
          </Typography>
        </Box>
      )}

      {attachments.length === 0 ? (
        <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 3 }}>
          No attachments yet
        </Typography>
      ) : (
        <List>
          {attachments.map((attachment) => (
            <ListItem
              key={attachment.id}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                mb: 1,
              }}
            >
              {thumbnails[attachment.id] ? (
                <Box
                  component="img"
                  src={thumbnails[attachment.id]}
                  alt={attachment.originalName}
                  sx={{
                    width: 40,
                    height: 40,
                    mr: 2,
                    borderRadius: 1,
                    objectFit: 'cover',
                    flexShrink: 0,
                  }}
                />
              ) : (
                <Box sx={{ mr: 2, fontSize: '1.5rem' }}>
                  {getFileIcon(attachment.mimeType)}
                </Box>
              )}
              <ListItemText
                primary={attachment.originalName}
                secondary={
                  <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                    <Chip label={formatFileSize(attachment.fileSize)} size="small" />
                    <Chip
                      label={new Date(attachment.createdAt).toLocaleDateString()}
                      size="small"
                    />
                    {attachment.uploader && (
                      <Chip label={`By ${attachment.uploader.name}`} size="small" />
                    )}
                  </Box>
                }
              />
              <ListItemSecondaryAction>
                <Tooltip title="Download">
                  <IconButton
                    edge="end"
                    onClick={() => handleDownload(attachment.id, attachment.originalName)}
                    sx={{ mr: 1 }}
                  >
                    <DownloadIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton
                    edge="end"
                    color="error"
                    onClick={() => handleDelete(attachment.id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      )}
    </Paper>
  );
};

export default TaskAttachments;
